import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PLATFORMS, normalizeSocialInput } from "./socialPlatforms";

// One field per platform. People type a username, a phone number or a link; when they leave
// the field (or press Enter) it is turned into a full link, or flagged if it can't be.
const SocialProfilesEditor = ({ links, onChange }) => {
    const { t } = useTranslation();
    const [drafts, setDrafts] = useState({}); // text being typed, by platform
    const [invalid, setInvalid] = useState({}); // platforms whose text could not be used

    const savedUrl = (key) => (links || []).find((l) => l.platform === key)?.url || "";
    const clearFlag = (key) => setInvalid((prev) => ({ ...prev, [key]: false }));
    const dropDraft = (key) =>
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });

    const commit = (key) => {
        if (!(key in drafts)) return; // nothing was typed

        const raw = drafts[key];
        if (raw.trim() === "") {
            if (savedUrl(key)) onChange(key, "");
            clearFlag(key);
            dropDraft(key);
            return;
        }

        const url = normalizeSocialInput(key, raw);
        if (!url) {
            setInvalid((prev) => ({ ...prev, [key]: true }));
            return;
        }

        if (url !== savedUrl(key)) onChange(key, url);
        clearFlag(key);
        dropDraft(key);
    };

    return (
        <div className="space-y-3">
            <p className="text-xs text-gray-500">{t("homeCard.social.hint")}</p>
            {PLATFORMS.map(({ key, label, Icon, placeholder, color }) => {
                const hasAccount = !!savedUrl(key);
                return (
                <div key={key}>
                    <div className="flex items-center gap-2">
                        <Icon
                            className={`text-lg w-6 ${hasAccount ? "" : "text-gray-500"}`}
                            style={hasAccount ? { color } : undefined}
                            aria-hidden="true"
                        />
                        <input
                            type="text"
                            aria-label={label}
                            aria-invalid={invalid[key] ? "true" : undefined}
                            placeholder={placeholder}
                            value={key in drafts ? drafts[key] : savedUrl(key)}
                            onChange={(e) => {
                                setDrafts((prev) => ({ ...prev, [key]: e.target.value }));
                                clearFlag(key);
                            }}
                            onBlur={() => commit(key)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    commit(key);
                                }
                            }}
                            className={`flex-1 px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 ${invalid[key]
                                ? "border-red-400 focus:ring-red-400"
                                : "border-gray-300 focus:ring-blue-500"
                                }`}
                        />
                    </div>
                    {invalid[key] && (
                        <p role="alert" className="mt-1 ml-8 text-xs text-red-600">
                            {t("homeCard.social.invalid")}
                        </p>
                    )}
                </div>
                );
            })}
        </div>
    );
};

export default SocialProfilesEditor;
